import React, { useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { useTranslate } from "@refinedev/core";

import { Modal } from "@shared/components";
import { IntegrationForm } from "../IntegrationForm";
import { PlanMapOptionsPanel } from "../PlanMapOptionsPanel";
import { EditIntegrationModalProps } from "./types";
import { useEditIntegrationModal } from "./controller";

const TABS = ["data", "plan_map_options"] as const;
type EditIntegrationTab = (typeof TABS)[number];

export const EditIntegrationModal = React.memo(
  ({ modalRef, onComplete }: EditIntegrationModalProps) => {
    const translate = useTranslate();
    const { methods, handleSubmit, isPending } = useEditIntegrationModal(
      modalRef,
      onComplete
    );
    const [integrationId, setIntegrationId] = useState<number | undefined>(
      undefined
    );
    const [tab, setTab] = useState<EditIntegrationTab>("data");

    return (
      <Modal
        ref={modalRef}
        title={translate("v1/integration_admin.titles.edit")}
        onConfirm={handleSubmit}
        methods={methods}
        isPending={isPending}
        onOpen={() => {
          // Read the id imperatively from the form right after Modal's
          // useModalController.open(row) calls reset(row) — that reset is a
          // synchronous mutation of the shared RHF store, so getValues() sees
          // it immediately. `id` isn't registered via any Controller (it's
          // not part of IntegrationFormValues), so a reactive useWatch on it
          // never fires: RHF only broadcasts field-change notifications for
          // registered fields. An imperative read here, timed by onOpen
          // (which Modal fires right after reset/setId), is what actually
          // works.
          const id = Number(methods.getValues("id" as any)) || undefined;
          setIntegrationId(id);
          setTab("data");
        }}
      >
        {integrationId && (
          <Tabs
            value={tab}
            onChange={(_, value: EditIntegrationTab) => setTab(value)}
            sx={{ mb: 2 }}
          >
            <Tab
              value="data"
              label={translate("v1/integration_admin.tabs.data")}
            />
            <Tab
              value="plan_map_options"
              label={translate("erp_integrations.plan_map_options.title")}
            />
          </Tabs>
        )}

        <div hidden={tab !== "data"}>
          <IntegrationForm integrationId={integrationId} />
        </div>

        {integrationId && tab === "plan_map_options" && (
          <PlanMapOptionsPanel integrationId={integrationId} />
        )}
      </Modal>
    );
  }
);
